import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Button,
  Flex,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUserData } from "../../hooks/useUserData";
import { SearchableMultiSelect } from "./SearchableMultiSelect";
import { CreateListForm } from "./CreateListForm";
import { BookmarkIcon } from "../icons/BookmarkIcon";
import { getComicLists, getReleaseLists } from "../../api/lists";
import { useListMutations } from "../../hooks/useListMutations";

interface AddToListButtonProps {
  entityType: "comic" | "release";
  entityId: number;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "tertiary";
}

export function AddToListButton({
  entityType,
  entityId,
  size = "sm",
  variant = "tertiary",
}: AddToListButtonProps) {
  const { userData } = useUserData();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const ref = useRef(null);
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const toast = useToast();

  const listType = entityType === "comic" ? "comics" : "releases";

  // Fetch user's lists
  const { data: listsResponse } = useQuery({
    enabled: userData?.is_admin,
    queryKey: [`user-${entityType}-lists`],
    queryFn: entityType === "comic" ? getComicLists : getReleaseLists,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Since we're using entity-specific endpoints, we get the lists directly
  const relevantLists = listsResponse?.data.payload?.results || [];

  // Use the list mutations hook
  const {
    createListMutation,
    addToListsMutation,
    removeFromListMutation,
    isLoading,
  } = useListMutations({
    entityType,
    entityId,
    onCreateSuccess: () => setIsCreateMode(false),
    onAddSuccess: () => setSelectedListIds([]),
    onClose,
    addCurrentEntityToNewList: true, // Automatically add current entity to new lists
  });

  const handleCreateList = (listData: {
    name: string;
    description?: string;
  }) => {
    createListMutation.mutate(listData);
  };

  const handleCreateCancel = () => {
    setIsCreateMode(false);
  };

  const handleCreateNewClick = () => {
    setIsCreateMode(true);
  };

  const handleApply = () => {
    if (selectedListIds.length === 0) {
      toast({
        title: "No lists selected",
        description: "Please select at least one list",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    addToListsMutation.mutate({ list_ids: selectedListIds });
  };

  const handleCancel = () => {
    setSelectedListIds([]);
    onClose();
  };

  const handleRemoveFromList = (listId: number) => {
    removeFromListMutation.mutate(listId);
  };

  // Only show for admin users
  if (!userData?.is_admin) {
    return null;
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      variant="filter"
      isLazy
    >
      <PopoverTrigger>
        <Button
          ref={ref}
          onClick={onToggle}
          variant={variant}
          size={size}
          leftIcon={<BookmarkIcon />}
        >
          Add to Lists
        </Button>
      </PopoverTrigger>

      <PopoverContent zIndex={1500} width="auto">
        <PopoverBody p={0}>
          {isCreateMode ? (
            <CreateListForm
              listType={listType}
              onCreateList={handleCreateList}
              onCancel={handleCreateCancel}
              isCreating={createListMutation.isPending}
            />
          ) : (
            <SearchableMultiSelect
              lists={relevantLists}
              selectedListIds={selectedListIds}
              onSelectionChange={setSelectedListIds}
              onRemoveFromList={handleRemoveFromList}
              listType={listType}
              entityId={entityId}
            />
          )}
        </PopoverBody>

        {!isCreateMode && (
          <PopoverFooter px={2} gap={8}>
            <Button variant="tertiary" size="sm" onClick={handleCreateNewClick}>
              New List +
            </Button>
            <Flex gap={2}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancel}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApply}
                isDisabled={selectedListIds.length === 0}
                isLoading={isLoading}
              >
                Add to Lists ({selectedListIds.length})
              </Button>
            </Flex>
          </PopoverFooter>
        )}
      </PopoverContent>
    </Popover>
  );
}
